import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { UserPlus, Trash2, Copy, Check, ShieldCheck, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const LEVELS = {
  full: { label: "Full Admin", desc: "Sees everything", cls: "bg-sage/15 text-sage" },
  orders: { label: "Order Handler", desc: "Order board only", cls: "bg-terracotta/15 text-terracotta" },
};

export default function AdminTeamPage() {
  const [members, setMembers] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "", access_level: "orders" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/admin/team").then((r) => setMembers(r.data.members || []));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/team", { ...f, password: f.password || null });
      setCreated({ email: data.member.email, temp_password: data.temp_password, access_level: data.member.access_level });
      setF({ name: "", email: "", password: "", access_level: "orders" });
      toast.success("Team member created");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
    finally { setBusy(false); }
  };

  const changeLevel = async (m, level) => {
    try { await api.patch(`/admin/team/${m.id}`, { access_level: level }); toast.success("Access level updated"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} (${m.email})? Their login stops working immediately.`)) return;
    try { await api.delete(`/admin/team/${m.id}`); toast.success("Team member removed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const copyCreds = async () => {
    try {
      await navigator.clipboard.writeText(`Email: ${created.email}\nPassword: ${created.temp_password}`);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      toast.success("Credentials copied");
    } catch { toast.error("Could not copy"); }
  };

  if (!members) return <div className="text-charcoal-muted">Loading team…</div>;

  return (
    <div data-testid="admin-team-page">
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-2xl text-charcoal">Team Members</h2>
        <p className="text-sm text-charcoal-muted mt-1">Invite staff to help run the marketplace. Only you (the owner) can manage the team.</p>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        {/* Invite form */}
        <div className="bg-white border border-border p-5 h-fit" data-testid="invite-team-form">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">
            <UserPlus className="w-4 h-4" /> Invite team member
          </div>
          <form onSubmit={invite} className="space-y-3">
            <input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-name" className="w-full border-2 border-border focus:border-terracotta bg-off-white px-3 py-2 outline-none text-sm" />
            <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-email" className="w-full border-2 border-border focus:border-terracotta bg-off-white px-3 py-2 outline-none text-sm" />
            <input placeholder="Password (leave blank to auto-generate)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="team-password" className="w-full border-2 border-border focus:border-terracotta bg-off-white px-3 py-2 outline-none text-sm" />
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-charcoal-muted font-semibold mb-1">Access level</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(LEVELS).map(([key, meta]) => (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setF({ ...f, access_level: key })}
                    data-testid={`team-level-${key}`}
                    className={`text-left border-2 p-2 transition-colors ${f.access_level === key ? "border-terracotta bg-terracotta/5" : "border-border hover:border-charcoal"}`}
                  >
                    <div className="text-xs font-semibold text-charcoal">{meta.label}</div>
                    <div className="text-[10px] text-charcoal-muted">{meta.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <button disabled={busy} data-testid="team-invite-submit" className="btn-terracotta w-full">{busy ? "Creating…" : "Create team member"}</button>
          </form>

          {created && (
            <div className="mt-4 bg-sage/10 border border-sage/40 p-3" data-testid="created-credentials">
              <div className="inline-flex items-center gap-1 text-xs font-semibold text-sage mb-1"><ClipboardList className="w-3.5 h-3.5" /> Share these credentials privately</div>
              <div className="text-xs text-charcoal"><span className="text-charcoal-muted">Email:</span> {created.email}</div>
              <div className="text-xs text-charcoal"><span className="text-charcoal-muted">Password:</span> <span className="font-mono">{created.temp_password}</span></div>
              <button onClick={copyCreds} data-testid="copy-creds-btn" className="mt-2 inline-flex items-center gap-1 text-xs border border-border hover:border-terracotta px-2 py-1 transition-colors">
                {copied ? <><Check className="w-3 h-3 text-sage" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
              <div className="text-[10px] text-charcoal-muted mt-2">They log in at the Admin login page with this email &amp; password.</div>
            </div>
          )}
        </div>

        {/* Members list */}
        <div className="bg-white border border-border" data-testid="team-list">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-heading font-semibold text-charcoal">Current team ({members.length})</h3>
          </div>
          {members.length === 0 ? (
            <div className="p-8 text-center text-sm text-charcoal-muted">No team members yet. Invite your first one on the left.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-off-white-alt text-xs uppercase tracking-[0.15em] text-charcoal-muted">
                  <tr>
                    <th className="text-left px-4 py-3">Member</th>
                    <th className="text-left px-4 py-3">Access level</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-t border-border" data-testid={`team-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-charcoal">{m.name}</div>
                        <div className="text-[11px] text-charcoal-muted">{m.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 ${LEVELS[m.access_level]?.cls || ""}`} data-testid={`team-level-badge-${m.id}`}>
                          <ShieldCheck className="w-3 h-3" /> {LEVELS[m.access_level]?.label || m.access_level}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2 flex-wrap items-center">
                          <select
                            value={m.access_level}
                            onChange={(e) => changeLevel(m, e.target.value)}
                            data-testid={`team-change-level-${m.id}`}
                            className="border border-border bg-off-white text-xs px-2 py-1 outline-none focus:border-terracotta"
                          >
                            <option value="orders">Order Handler</option>
                            <option value="full">Full Admin</option>
                          </select>
                          <button onClick={() => remove(m)} data-testid={`team-remove-${m.id}`} className="text-xs border border-destructive/40 text-destructive hover:bg-destructive/10 px-2 py-1 inline-flex items-center gap-1 transition-colors">
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
